/**
 * A record's documents — §11's presigned upload/download pair, rendered as a
 * plain attach/list/download/remove panel. Generic over `entityKey`/
 * `recordId` (the same pair `docs.attachment` keys on) so it drops into any
 * detail screen — account, opportunity, whichever asks for it next — without
 * copying the upload dance per screen.
 */
import { useRef, useState } from 'react';
import { EmptyState, ButtonSpinner } from '../../components/common/index.jsx';
import { SkeletonText } from '../../components/common/Loading.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { fileSize, dateTime } from '../../utils/formatters.js';
import {
  useAttachments, useUploadDocument, useDownloadDocument, useDeleteDocument, MAX_UPLOAD_BYTES,
} from './useDocuments.js';

const EXT_ICON = {
  pdf: 'bi-file-earmark-pdf-fill', doc: 'bi-file-earmark-word-fill', docx: 'bi-file-earmark-word-fill',
  xls: 'bi-file-earmark-excel-fill', xlsx: 'bi-file-earmark-excel-fill',
  ppt: 'bi-file-earmark-ppt-fill', pptx: 'bi-file-earmark-ppt-fill',
  png: 'bi-file-earmark-image-fill', jpg: 'bi-file-earmark-image-fill', jpeg: 'bi-file-earmark-image-fill',
};

function fileIcon(filename) {
  const ext = filename.split('.').pop()?.toLowerCase();
  return EXT_ICON[ext] ?? 'bi-file-earmark-fill';
}

export function DocumentsPanel({ entityKey, recordId }) {
  const { can } = useAuth();
  const canUpload = can('documents', 'create');
  const canDelete = can('documents', 'delete');

  const { data, isLoading, error } = useAttachments(entityKey, recordId);
  const upload = useUploadDocument(entityKey, recordId);
  const download = useDownloadDocument();
  const remove = useDeleteDocument(entityKey, recordId);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const inputRef = useRef(null);

  const attachments = data?.data ?? [];

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be picked again after an error
    if (file) upload.mutate(file);
  };

  const onDelete = (attachmentId) => {
    setPendingDeleteId(attachmentId);
    remove.mutate(attachmentId, { onSettled: () => setPendingDeleteId(null) });
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="t-2xs ink-4">
          <i className="bi bi-hdd-network me-1" aria-hidden="true" />
          Stored in MinIO · max {fileSize(MAX_UPLOAD_BYTES)} per file
        </div>
        {canUpload && (
          <>
            <input ref={inputRef} type="file" hidden onChange={onFileChosen} />
            <button
              type="button"
              className="btn btn-add btn-sm-x"
              disabled={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              {upload.isPending ? <><ButtonSpinner className="me-1" />Uploading…</> : <><i className="bi bi-upload me-1" />Upload document</>}
            </button>
          </>
        )}
      </div>

      {isLoading && <SkeletonText lines={3} />}
      {error && <div className="t-sm ink-3">Could not load documents.</div>}

      {!isLoading && !error && attachments.length === 0 && (
        <EmptyState
          icon="bi-folder2-open" title="No documents yet"
          message={canUpload ? 'Upload contracts, proposals or any other file for this record.' : 'Nothing has been attached to this record yet.'}
        />
      )}

      {attachments.map((a) => (
        <div key={a.attachment_id} className="d-flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid var(--line-2)' }}>
          <i className={`bi ${fileIcon(a.filename)}`} style={{ fontSize: 20, color: 'var(--ink-4)' }} aria-hidden="true" />
          <div className="min-w-0 flex-grow-1">
            <div className="t-sm fw-650 text-truncate">{a.filename}</div>
            <div className="t-2xs ink-4">
              {fileSize(a.byte_size)} · {a.uploaded_by?.full_name ?? 'Unknown'} · {dateTime(a.attached_at)}
            </div>
          </div>
          {!a.servable && <span className="pill pill-gray no-wrap">Not ready</span>}
          <button
            type="button"
            className="btn btn-soft btn-sm-x"
            disabled={!a.servable || download.isPending}
            onClick={() => download.mutate(a.object_id)}
            title="Download"
          >
            <i className="bi bi-download" aria-hidden="true" />
          </button>
          {canDelete && (
            <button
              type="button"
              className="btn btn-delete btn-sm-x"
              disabled={remove.isPending && pendingDeleteId === a.attachment_id}
              onClick={() => onDelete(a.attachment_id)}
              title="Remove"
            >
              {remove.isPending && pendingDeleteId === a.attachment_id
                ? <ButtonSpinner />
                : <i className="bi bi-trash3" aria-hidden="true" />}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
